import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../components/Button';
import { Spinner } from '../components/Spinner';
import { api } from '../services/api';
import { ApiError, type UploadResponse } from '../services/types';
import { useAppStore } from '../store/appStore';

type FlowState =
  | { kind: 'idle' }
  | { kind: 'uploading'; file: File }
  | { kind: 'creating'; file: File; upload: UploadResponse }
  | { kind: 'error'; message: string };

/**
 * Landing page.
 *
 * One job: accept a CSV / Excel file, call `/upload` then `/runs`, and
 * redirect the reviewer to `/runs/:id` on success. Everything else (FIA,
 * schema, manifest, HITL) lives on the run detail page.
 */
export function UploadPage() {
  const navigate = useNavigate();
  const setCurrentRunId = useAppStore((s) => s.setCurrentRunId);
  const [state, setState] = useState<FlowState>({ kind: 'idle' });

  // v0.6 Stage B7 — "Clear memory" affordance. The carry-forward store
  // skips HITLs the reviewer already answered on prior same-bytes runs,
  // which is correct for production but blocks testing the HITL flow
  // against a re-uploaded file. The button hides when there's nothing
  // to clear so the reviewer isn't offered a no-op action. Feedback is
  // intentionally inline (button label flips to "Memory cleared" for 2s)
  // — no toast, no modal.
  const [decisionCount, setDecisionCount] = useState<number | null>(null);
  const [clearLabel, setClearLabel] =
    useState<'idle' | 'clearing' | 'cleared'>('idle');
  const clearedTimerRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getDecisionCount()
      .then((res) => {
        if (!cancelled) setDecisionCount(res.count);
      })
      .catch(() => {
        // Best-effort — a failed count just hides the button. The
        // upload flow itself is unaffected.
        if (!cancelled) setDecisionCount(0);
      });
    return () => {
      cancelled = true;
      if (clearedTimerRef.current !== null) {
        window.clearTimeout(clearedTimerRef.current);
      }
    };
  }, []);

  async function onClearMemory() {
    setClearLabel('clearing');
    try {
      await api.clearDecisions();
      setDecisionCount(0);
      setClearLabel('cleared');
      clearedTimerRef.current = window.setTimeout(() => {
        setClearLabel('idle');
        clearedTimerRef.current = null;
      }, 2000);
    } catch {
      // Surface failures by reverting the label; the user can retry.
      setClearLabel('idle');
    }
  }

  async function onFileChosen(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setState({ kind: 'uploading', file });
      const upload = await api.uploadFile(file);

      setState({ kind: 'creating', file, upload });
      const run = await api.createRun({
        file_id: upload.file_id,
        cpa_config: { profile_name: 'default' },
      });

      setCurrentRunId(run.run_id);
      navigate(`/runs/${encodeURIComponent(run.run_id)}/fia`);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API ${err.status}: ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Unknown error';
      setState({ kind: 'error', message });
    }
  }

  return (
    <section aria-labelledby="upload-title" className="space-y-6">
      <div>
        <h1 id="upload-title" className="mb-2">
          Upload a file to start a run
        </h1>
        <p className="text-muted max-w-2xl">
          Drop a CSV or Excel file. The pipeline will identify the format (FIA), infer a schema
          (RAS), build an execution manifest (MOA), and surface anything ambiguous for human review.
          You can walk away from the run and come back to it on this same URL.
        </p>
      </div>

      <div className="rounded-lg border border-border bg-paper shadow-card p-6">
        <label
          htmlFor="file-input"
          className="block border-2 border-dashed border-border rounded-md px-6 py-10 text-center cursor-pointer hover:border-brand transition"
        >
          <div className="text-base font-medium mb-1">Choose a CSV or Excel file</div>
          <div className="text-sm text-muted">or drop one here</div>
          <input
            id="file-input"
            type="file"
            accept=".csv,.xlsx,.xls,text/csv"
            className="sr-only"
            onChange={onFileChosen}
            disabled={state.kind === 'uploading' || state.kind === 'creating'}
          />
        </label>

        {(
          // Show when there's something to clear, OR while we're
          // mid-action / showing the "Memory cleared" feedback — the
          // user just clicked, the button vanishing mid-feedback would
          // be confusing. After the 2s timeout reverts clearLabel to
          // "idle", the (count > 0) check applies again and the button
          // hides naturally.
          decisionCount !== null && decisionCount > 0
        ) || clearLabel !== 'idle' ? (
          <div className="mt-3 flex justify-end">
            <Button
              variant="ghost"
              onClick={() => void onClearMemory()}
              disabled={clearLabel !== 'idle'}
              data-testid="clear-memory-button"
              title="Wipe all stored HITL decisions so re-uploading the same file goes through the HITLs again"
            >
              {clearLabel === 'clearing'
                ? 'Clearing…'
                : clearLabel === 'cleared'
                  ? 'Memory cleared'
                  : 'Clear memory'}
            </Button>
          </div>
        ) : null}

        <div className="mt-6" aria-live="polite">
          {state.kind === 'uploading' && (
            <InlineStatus>
              <Spinner /> Uploading <strong>{state.file.name}</strong>…
            </InlineStatus>
          )}
          {state.kind === 'creating' && (
            <InlineStatus>
              <Spinner /> Creating run for <strong>{state.upload.filename}</strong>…
            </InlineStatus>
          )}
          {state.kind === 'error' && (
            <div role="alert" className="space-y-3">
              <div className="text-danger">{state.message}</div>
              <Button variant="secondary" onClick={() => setState({ kind: 'idle' })}>
                Try again
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function InlineStatus({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center gap-3 text-muted">{children}</div>;
}
